import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import reviewApi from "../api/reviewApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import {
  PERFORMANCE_QUERY_KEYS,
  REVIEW_STATUSES,
} from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";
import useGoalCycleOptions from "../hooks/useGoalCycleOptions";
import useReviewTemplateOptions from "../hooks/useReviewTemplateOptions";
import { personName } from "../utils/normalize";

const PerformanceReviewsPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const { goalCycleOptions, isLoading: goalCyclesLoading } =
    useGoalCycleOptions();
  const { reviewTemplateOptions, isLoading: reviewTemplatesLoading } =
    useReviewTemplateOptions();
  const [showGuide, setShowGuide] = React.useState(false);
  const [status, setStatus] = React.useState("");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadReviews = can(PERFORMANCE_PERMISSIONS.REVIEWS_READ);
  const canCreateReviews = can(PERFORMANCE_PERMISSIONS.REVIEWS_CREATE);

  if (!canReadReviews) {
    return (
      <ModulePageShell
        title="Performance Reviews"
        description="Create and process review cycles including recommendation workflows."
      >
        <EmptyState message="You do not have permission to view performance reviews." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.reviews, status],
    queryFn: () =>
      reviewApi.listReviews({
        finalStatus: status || undefined,
        page: 1,
        limit: 50,
      }),
  });

  const createMutation = useMutation({
    mutationFn: reviewApi.createReview,
    onSuccess: () => {
      toast({ title: "Review created", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.reviews],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create review",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      employeeId: "",
      managerId: "",
      goalCycleId: "",
      reviewTemplateId: "",
      overallRating: 0,
      goalScore: 0,
      competencyScore: 0,
      strengths: "",
      improvements: "",
      managerRemarks: "",
      employeeRemarks: "",
      finalStatus: "DRAFT",
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Performance Reviews"
      description="Create and process review cycles including recommendation workflows."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canCreateReviews ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) => createMutation.mutate(values))}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Create Review</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Employee ID</FormLabel>
                  <Select
                    placeholder="Select employee"
                    {...register("employeeId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Manager ID</FormLabel>
                  <Select
                    placeholder="Select manager"
                    {...register("managerId")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Goal Cycle</FormLabel>
                  <Select
                    placeholder="Select goal cycle"
                    {...register("goalCycleId")}
                    isDisabled={goalCyclesLoading}
                  >
                    {goalCycleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Review Template</FormLabel>
                  <Select
                    placeholder="Select review template"
                    {...register("reviewTemplateId")}
                    isDisabled={reviewTemplatesLoading}
                  >
                    {reviewTemplateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select {...register("finalStatus")}>
                    {REVIEW_STATUSES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Create Review
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to reviews, but cannot create reviews." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        <HStack justify="space-between" mb={3}>
          <Text fontWeight="semibold">Review Listing</Text>
          <Select
            maxW="220px"
            placeholder="Filter status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {REVIEW_STATUSES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </HStack>

        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No reviews found." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Employee</Th>
                <Th>Cycle</Th>
                <Th>Manager</Th>
                <Th>Status</Th>
                <Th>Rating</Th>
                <Th>Increment</Th>
                <Th>Promotion</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{personName(item.employeeId || item.employeeSnapshot)}</Td>
                  <Td>{item.goalCycleId?.name || item.goalCycleId || "-"}</Td>
                  <Td>{personName(item.managerId || item.reviewerId)}</Td>
                  <Td>{item.finalStatus || "-"}</Td>
                  <Td>{item.overallRating ?? 0}</Td>
                  <Td>
                    {item.incrementRecommendation?.recommended ? "Yes" : "No"}
                  </Td>
                  <Td>
                    {item.promotionRecommendation?.recommended ? "Yes" : "No"}
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        navigate(`/performance/reviews/${item._id}`)
                      }
                    >
                      View
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default PerformanceReviewsPage;
